import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';

import { useDeleteGateway } from 'src/features/gateway/api/useDeleteGateway';
import { LIST_GATEWAYS_RESOURCE } from 'src/features/gateway/api/useListGateways';
import { Gateway } from 'src/features/gateway/utils/types';
import { gatewayMessages } from 'src/features/gateway/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalErrorMessages, generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';

interface GatewayFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedGateway: Gateway;
    children?: React.ReactNode;
}

function DeleteGatewayFormModal<TData>({ selectedGateway, semantics, children }: GatewayFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { mutate: deleteGateway, isPending: isPendingDelete } = useDeleteGateway({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_GATEWAYS_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(gatewayMessages.deleteGatewaySuccess),
                description: formatMessage(gatewayMessages.deleteGatewaySuccessDescription, {
                    entry: selectedGateway.translations.find((translation) => translation.language === locale)?.name,
                }),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const onSubmit = () => deleteGateway({ gatewayId: selectedGateway.id, parkingId });

    return (
        <Dialog modal open={isModalOpen} onOpenChange={setIsModalOpen}>
            {children && <DialogTrigger asChild>{children}</DialogTrigger>}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.DELETE} />
                        <div>
                            <DialogTitle>
                                <FormattedMessage
                                    id={generalMessages.deleteEntry.id}
                                    defaultMessage={generalMessages.deleteEntry.defaultMessage}
                                    values={{ entry: semantics?.entryName?.toLowerCase() }}
                                />
                            </DialogTitle>
                            <DialogDescription>
                                <FormattedMessage
                                    id={gatewayMessages.deleteGatewaySubtitle.id}
                                    defaultMessage={gatewayMessages.deleteGatewaySubtitle.defaultMessage}
                                    values={{
                                        entry: selectedGateway.translations.find(
                                            (translation) => translation.language === locale,
                                        )?.name,
                                    }}
                                />
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <p className="text-gray-500">
                        <FormattedMessage
                            id={gatewayMessages.confirmDeleteGateway.id}
                            defaultMessage={gatewayMessages.confirmDeleteGateway.defaultMessage}
                            values={{
                                entry: selectedGateway.translations.find(
                                    (translation) => translation.language === locale,
                                )?.name,
                            }}
                        />
                    </p>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="destructiveLight"
                        disabled={isPendingDelete}
                        type="submit"
                        onClick={onSubmit}
                        className="flex-1"
                    >
                        {isPendingDelete && (
                            <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />
                        )}
                        <FormattedMessage
                            id={generalMessages.deleteEntry.id}
                            defaultMessage={generalMessages.deleteEntry.defaultMessage}
                            values={{ entry: semantics?.entryName?.toLowerCase() }}
                        />
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default DeleteGatewayFormModal;
